import React from 'react';
import { Dialog } from './ui/Dialog';
import { Button } from './ui/Button';

export interface ConfirmDialogProps {
  /** Main question/title of the dialog */
  title: string;
  /** Optional additional message lines shown below the title */
  message?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Marks the confirm button as a destructive action (red styling) */
  danger?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * A custom styled confirmation modal, built on the shared `Dialog` primitive
 * with `role="alertdialog"`.
 *
 * IMPORTANT: Do not use `window.confirm()`/`window.alert()` for confirmations that matter
 * (e.g. deleting a subscription). Native confirm/alert dialogs are unreliable or silently
 * suppressed in embedded WebView contexts such as the Telegram Mini-App WebView used by
 * TelegramSubscriptionPage.tsx - `window.confirm()` there can return `false`/undefined
 * immediately without ever showing anything to the user, which makes actions relying on it
 * (like delete) appear to silently do nothing. This component renders a real DOM modal that
 * works consistently everywhere (issue #365).
 */
export const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  title,
  message,
  confirmLabel = 'Bestätigen',
  cancelLabel = 'Abbrechen',
  danger = false,
  onConfirm,
  onCancel,
}) => (
  <Dialog
    open
    onClose={onCancel}
    title={title}
    role="alertdialog"
    size="sm"
    overlayClassName="modal-overlay"
    footer={
      <>
        <Button type="button" variant="ghost" onClick={onCancel}>
          {cancelLabel}
        </Button>
        <Button type="button" variant={danger ? 'danger' : 'primary'} onClick={onConfirm} autoFocus>
          {confirmLabel}
        </Button>
      </>
    }
  >
    {message && <p className="whitespace-pre-line text-sm text-muted">{message}</p>}
  </Dialog>
);
